// Bay coordinates from a layout model — the browser's copy.
//
// This deliberately duplicates Portal-BE/app/warehouse/services/layout-geometry.js.
// The BE recomputes and stores coordinates on every save and remains the
// authority; this copy exists so dragging a corridor or changing a bay width
// redraws instantly instead of costing a round trip per pixel.
//
// The two MUST agree. If they drift, the editor shows one layout and the
// routing uses another, which is the worst possible failure here because it
// looks fine. layoutGeometry.test.ts pins the same cases as the backend's
// layout-geometry.test.js — change the rules in one place and both suites
// should be updated together.

export interface LayoutRun {
    seq: number;
    /** Width of the whole shelving unit between uprights. */
    shelf_width_m: number;
    /** Bays on that shelf — "a bay is a location per shelf". Bay pitch is
     *  therefore shelf_width_m / locations_per_shelf. */
    locations_per_shelf: number;
    upright_width_m: number;
    /** Levels reachable by hand, picked on the low tier. */
    low_shelves: number;
    /** Levels above them, picked on the high tier. */
    high_shelves: number;
    shelf_height_m: number;
    shelf_gap_height_m: number;
}

/** Width of one bay: the shelf divided by the locations on it. */
export const bayWidthOf = (run: LayoutRun): number =>
    run.shelf_width_m / Math.max(1, run.locations_per_shelf);

export interface LayoutAisle {
    aisle: string;
    corridor_id: string;
    side: "a" | "b";
    racking_type: "pallet" | "shelving";
    rack_depth_m: number;
    /** True where this face shares its rack with the aisle on the other side
     *  (H onward at Pembroke): one physical structure, two pick faces. */
    shared_rack?: boolean;
    origin_offset_m: number;
    bay_one_at: "origin" | "far";
    travel: "asc" | "desc";
    bays: string[];
    /** The HIGH tier's own bays. F and G hold 30 pallet bays above 58 and 53
     *  shelving ones, so the high tier cannot borrow the low list. */
    high_bays?: string[];
    runs: LayoutRun[];
    /** The HIGH tier's own racking, where it differs from the low one. F and G
     *  are shelving below and pallet beams above. Empty means "same footprint
     *  as low", which is true of every other aisle. */
    high_runs?: LayoutRun[];
}

export interface LayoutCorridor {
    id: string;
    zone: string | null;
    aisle_a: string | null;
    aisle_b: string | null;
    x_m: number;
    width_m: number;
}

export interface LayoutCrossing {
    id: string | null;
    kind: "end" | "tunnel";
    /** Which end of the aisles an END cross-aisle runs along. Its real shape
     *  is the polyline from endPath(), not a single at_m. */
    side?: "origin" | "far";
    /** A tunnel is a gap cut into a physical run, so it is welded to one
     *  rather than floating at a metre position: it sits at the boundary
     *  before `run_seq` in `aisle`, and its at_m is derived from that run.
     *  Re-measure a bay and the tunnel travels with its shelf. */
    anchor?: { aisle: string; run_seq: number } | null;
    /** Which shelf POSITION a tunnel occupies, from the origin end. Negative
     *  counts back from the far end, so -1 is the last shelf whatever an
     *  aisle's shelf count — which is how one tunnel spans A (11 shelves) and
     *  B-G (10). Naming a shelf rather than a distance is what makes it
     *  sticky. */
    at_shelf?: number;
    at_m: number;
    width_m: number;
    label: string | null;
    connects: string[];
}

export interface LayoutPoint {
    id: string | null;
    kind: string;
    x_m: number;
    y_m: number;
    label: string | null;
    barcode: string | null;
}

export interface LayoutObstacle {
    id: string | null;
    x_m: number; y_m: number; w_m: number; h_m: number;
    label: string | null;
}

export interface LayoutModel {
    schema_version: number;
    walk_speed_mps: number;
    turn_cost_s: number;
    stop_start_cost_s: number;
    cage_mode: "with_picker" | "staged_at_end" | "none";
    corridors: LayoutCorridor[];
    aisles: LayoutAisle[];
    crossings: LayoutCrossing[];
    points: LayoutPoint[];
    obstacles: LayoutObstacle[];
}

export interface PlacedBay {
    aisle: string;
    bay: string;
    corridor_id: string;
    side: "a" | "b";
    along_m: number | null;
    x_m: number | null;
    y_m: number | null;
    width_m: number | null;
    run_seq: number | null;
    unplaced: boolean;
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;

/**
 * Bays in the order they physically sit along the corridor, origin end first.
 *
 * `bays` is stored ascending by bay number. Where bay 1 sits at the far end —
 * Pembroke numbers some aisles from either end — physical order is the reverse.
 * Does not mutate the stored array.
 */
export const baysInPhysicalOrder = (aisle: LayoutAisle): string[] =>
    aisle.bay_one_at === "far" ? [...aisle.bays].reverse() : aisle.bays;

/** Where the aisle's racking ends, measured from the corridor origin.
 *
 *  The upright is charged once per RUN, not once per bay: a run is one
 *  continuous shelving unit with a 75mm post at its end. Charging it per bay
 *  would stretch a 7-bay run by half a metre. Mirrors the backend's
 *  layout-geometry.js — the two must agree or the editor draws something the
 *  server will not reproduce. */
/** The runs to draw for a tier — high falls back to low where it matches.
 *
 *  THERE ARE NO TUNNELS UP HIGH: a tunnel is a gap cut through the racking at
 *  floor level for a picker to walk through, and above it the shelving carries
 *  straight on. So a tunnel position inherited from the low runs is filled
 *  back in with racking rather than left as a hole. Mirrors the backend. */
export const runsForTier = (aisle: LayoutAisle, tier: "low" | "high" = "low"): LayoutRun[] => {
    if (tier !== "high") return aisle.runs;
    if ((aisle.high_runs ?? []).length > 0) return aisle.high_runs!;
    if (!aisle.runs.some(r => r.locations_per_shelf === 0)) return aisle.runs;
    const racked = aisle.runs.find(r => r.locations_per_shelf > 0);
    if (!racked) return aisle.runs;
    return aisle.runs.map(r => (r.locations_per_shelf > 0 ? r : {
        ...r,
        locations_per_shelf: racked.locations_per_shelf,
        low_shelves: 0,
        high_shelves: racked.high_shelves,
    }));
};

export const aisleLength = (aisle: LayoutAisle, tier: "low" | "high" = "low"): number =>
    aisle.origin_offset_m
    + runsForTier(aisle, tier).reduce((n, r) => n + r.shelf_width_m + r.upright_width_m, 0);

/** Where each run starts and ends — the only positions a tunnel may sit at. */
export const runBoundaries = (
    aisle: LayoutAisle,
): { run_seq: number; start_m: number; end_m: number }[] => {
    const out: { run_seq: number; start_m: number; end_m: number }[] = [];
    let cursor = aisle.origin_offset_m;
    for (const run of aisle.runs) {
        const start = cursor;
        cursor += run.shelf_width_m;
        out.push({
            run_seq: run.seq,
            start_m: Math.round(start * 1000) / 1000,
            end_m: Math.round(cursor * 1000) / 1000,
        });
        cursor += run.upright_width_m;
    }
    return out;
};

/** A crossing's effective position: an anchored tunnel derives it from the run
 *  it is welded to, so it follows that shelf when the geometry changes. */
export const resolvedAt = (model: LayoutModel, c: LayoutCrossing): number => {
    if (!c.anchor) return c.at_m;
    const aisle = model.aisles.find(a => a.aisle === c.anchor!.aisle);
    if (!aisle) return c.at_m;
    const bounds = runBoundaries(aisle);
    const hit = bounds.find(b => b.run_seq === c.anchor!.run_seq);
    return hit ? hit.start_m : (bounds[bounds.length - 1]?.end_m ?? c.at_m);
};

/** Snap a dragged position to the nearest shelf edge, and say which run it
 *  belongs to so the tunnel stays stuck to it. */
export const snapToRun = (
    aisle: LayoutAisle, alongM: number,
): { anchor: { aisle: string; run_seq: number } | null; at_m: number } => {
    const bounds = runBoundaries(aisle);
    if (bounds.length === 0) return { anchor: null, at_m: alongM };
    let best = bounds[0];
    let bestDist = Math.abs(alongM - bounds[0].start_m);
    for (const b of bounds) {
        const d = Math.abs(alongM - b.start_m);
        if (d < bestDist) { best = b; bestDist = d; }
    }
    const last = bounds[bounds.length - 1];
    if (Math.abs(alongM - last.end_m) < bestDist) {
        return { anchor: { aisle: aisle.aisle, run_seq: last.run_seq + 1 }, at_m: last.end_m };
    }
    return { anchor: { aisle: aisle.aisle, run_seq: best.run_seq }, at_m: best.start_m };
};

/** Resolve a tunnel's shelf index within one aisle. */
export const tunnelShelfIndex = (aisle: LayoutAisle, atShelf: number | undefined): number => {
    const n = aisle.runs.length;
    if (n === 0) return 0;
    const i = Math.trunc(atShelf ?? -1);
    return i < 0 ? Math.max(0, n + i) : Math.min(i, n - 1);
};

/** The path a TUNNEL takes across the shed, left to right.
 *
 *  Same shape as an end cross-aisle: cut through several corridors at once,
 *  and the shelf it replaces sits at a different metre position in each
 *  because shelf widths differ — so it goes diagonal where two neighbours'
 *  shelves do not line up. `connects` empty means every corridor. */
export const tunnelPath = (
    model: LayoutModel, crossing: LayoutCrossing,
): { corridor_id: string; x_m: number; along_m: number }[] => {
    const wanted = new Set(crossing.connects ?? []);
    const byCorridor = new Map<string, number>();
    for (const a of model.aisles) {
        if (wanted.size > 0 && !wanted.has(a.corridor_id)) continue;
        const bounds = runBoundaries(a);
        if (bounds.length === 0) continue;
        const b = bounds[tunnelShelfIndex(a, crossing.at_shelf)];
        if (!b) continue;
        const centre = (b.start_m + b.end_m) / 2;
        const cur = byCorridor.get(a.corridor_id);
        byCorridor.set(a.corridor_id, cur === undefined ? centre : (cur + centre) / 2);
    }
    return model.corridors
        .filter(c => byCorridor.has(c.id))
        .map(c => ({
            corridor_id: c.id, x_m: c.x_m,
            along_m: Math.round(byCorridor.get(c.id)! * 1000) / 1000,
        }))
        .sort((a, b) => a.x_m - b.x_m);
};

/** Nearest shelf index to a position along an aisle — what a dragged tunnel
 *  snaps to. */
export const shelfIndexAt = (aisle: LayoutAisle, alongM: number): number => {
    const bounds = runBoundaries(aisle);
    if (bounds.length === 0) return 0;
    let best = 0;
    let bestDist = Infinity;
    bounds.forEach((b, i) => {
        const d = Math.abs(alongM - (b.start_m + b.end_m) / 2);
        if (d < bestDist) { bestDist = d; best = i; }
    });
    return best;
};

/** The path an end cross-aisle takes across the shed, left to right.
 *
 *  It runs along the ENDS of the aisles and adjacent corridors need not end
 *  flush, so where two neighbours differ the joining segment is diagonal —
 *  which is what the picker actually walks. A single at_m would have to pick
 *  one corridor's end and be wrong about every other. */
export const endPath = (
    model: LayoutModel, side: "origin" | "far" = "origin",
): { corridor_id: string; x_m: number; along_m: number }[] => {
    const spans = new Map<string, { start: number; end: number }>();
    for (const a of model.aisles) {
        const bounds = runBoundaries(a);
        if (bounds.length === 0) continue;
        const start = bounds[0].start_m;
        const end = bounds[bounds.length - 1].end_m;
        const cur = spans.get(a.corridor_id);
        if (!cur) spans.set(a.corridor_id, { start, end });
        else { cur.start = Math.min(cur.start, start); cur.end = Math.max(cur.end, end); }
    }
    return model.corridors
        .filter(c => spans.has(c.id))
        .map(c => ({
            corridor_id: c.id,
            x_m: c.x_m,
            along_m: side === "far" ? spans.get(c.id)!.end : spans.get(c.id)!.start,
        }))
        .sort((a, b) => a.x_m - b.x_m);
};

/**
 * Every bay in the model, with coordinates.
 *
 * Runs partition the aisle's bay list in PHYSICAL order: run 1 is the
 * `bay_count` bays nearest the corridor origin. That is what lets a run of 4,
 * 6 or 7 bays carry its own bay width, which is the whole reason two sides of
 * one corridor can hold different numbers of bays.
 *
 * A bay no run reaches comes back `unplaced` rather than being dropped, so the
 * editor can say "6 bays have no geometry" instead of quietly losing them.
 */
export const computeBays = (
    model: LayoutModel, tier: "low" | "high" = "low",
): PlacedBay[] => {
    const corridors = new Map(model.corridors.map(c => [c.id, c]));
    const out: PlacedBay[] = [];

    for (const aisle of model.aisles) {
        const corridor = corridors.get(aisle.corridor_id);
        if (!corridor) continue;
        // The pick face is the corridor edge; rack depth extends away from it
        // and only matters to the drawing.
        const faceX = aisle.side === "b"
            ? corridor.x_m + corridor.width_m / 2
            : corridor.x_m - corridor.width_m / 2;

        const ordered = baysInPhysicalOrder(
            tier === "high" && (aisle.high_bays ?? []).length > 0
                ? { ...aisle, bays: aisle.high_bays! }
                : aisle,
        );
        let cursor = aisle.origin_offset_m;
        let i = 0;

        for (const run of runsForTier(aisle, tier)) {
            const gap = bayWidthOf(run);
            // A run occupies its whole shelf width whether or not it holds bays.
            // A TUNNEL shelf holds none, and advancing per-bay meant its gap
            // took up no space at all — every bay beyond it slid a shelf closer
            // to the origin. Mirrors the backend fix.
            const runStart = cursor;
            for (let k = 0; k < run.locations_per_shelf; k++, i++) {
                const bay = ordered[i];
                if (bay !== undefined) {
                    const along = cursor + gap / 2;
                    out.push({
                        aisle: aisle.aisle, bay, corridor_id: aisle.corridor_id,
                        side: aisle.side,
                        along_m: round3(along), x_m: round3(faceX), y_m: round3(along),
                        width_m: round3(gap), run_seq: run.seq, unplaced: false,
                    });
                }
                cursor += gap;
            }
            cursor = runStart + run.shelf_width_m + run.upright_width_m;
        }

        for (; i < ordered.length; i++) {
            out.push({
                aisle: aisle.aisle, bay: ordered[i], corridor_id: aisle.corridor_id,
                side: aisle.side,
                along_m: null, x_m: null, y_m: null, width_m: null,
                run_seq: null, unplaced: true,
            });
        }
    }

    return out;
};
